import {type RunColorConfig} from '@wandb/weave/common/util/section';
import {useCallback, useMemo} from 'react';

import {type SemanticLegendSettingsWithCategoricalConfig} from '../../../components/WorkspaceDrawer/Settings/types';
import {type CustomRunNames} from '../../../state/views/customRunNames/types';
import {type Key} from '../../../util/runTypes';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {runsLinePlotConfigDefaults} from '../defaults';
import {usePanelConfigContext} from '../PanelConfigContext';
import {useRunsLinePlotContext} from '../RunsLinePlotContext/RunsLinePlotContext';
import {usePanelGroupingSettings} from '../RunsLinePlotContext/usePanelGroupingSettings';
import {type RunsLinePlotConfig} from '../types';
import {useColorByMetrics} from '../useColorByMetrics';
import {type Timestep} from './../../../util/plotHelpers/types';
import {type RunSetQuery} from './../../../util/queryTypes';
import {type LineConfig} from './types';
// import {useDerivedLinesConfigDebugging} from './useDerivedLinesConfigDebugging';

/**
 * Changing values in the grouping tab in a panel runs line plot
 * 1. Toggling the `Runs` switch -> flips the `aggregate` value
 * 2. Changing the `Group By` option ->   changes the `panelGroupKeys` or `groupBy`
 *  - `groupBy` is the property on the config object, panelGroupKeys is a custom naming convention set in the outliers wrapper
 * 3. Changing the `Agg` boxes -> changes the `groupLine` setting
 * 4. Changing the `Range` boxes -> changes the `groupArea` setting
 * 5. Toggling the `Metrics` switch -> changes the `aggregateMetrics` setting
 */

export const useDerivedLinesConfiguration = ({
  customRunColors,
  customRunNames,
  runsLinePlotConfig,
  runSets,
  zoomTimestep,
  semanticLegendSettings,
}: {
  customRunColors: RunColorConfig | undefined;
  customRunNames?: CustomRunNames;
  runsLinePlotConfig: RunsLinePlotConfig;
  runSets: RunSetQuery[];
  zoomTimestep: Timestep | null;
  semanticLegendSettings: SemanticLegendSettingsWithCategoricalConfig;
}): LineConfig => {
  const {
    aggregateCalculations,
    getDefaultLegendTemplateByRunsetId,
    entityName,
    excludeOutliers,
    limit,
    parsedExpressions,
    projectName,
    showOriginalAfterSmoothing,
    smoothingType,
    smoothingWeight,
    useRunsTableGroupingInPanels,
    xAxis,
    yLogScale,
    xAxisFormat,
  } = usePanelConfigContext();
  const {isSingleRun} = useSingleRunModeContext();
  const {aggregatePanelRuns, aggregatePanelMetrics, getGroupKeysByRunsetId} =
    usePanelGroupingSettings();

  const {windowing} = useRunsLinePlotContext();

  const aggregateMetrics =
    aggregatePanelMetrics ?? runsLinePlotConfigDefaults.aggregateMetrics;

  const getLegendTemplateByRunsetId = useCallback(
    (runsetId: string | undefined) =>
      isSingleRun
        ? getDefaultLegendTemplateByRunsetId(runsetId)
        : runsLinePlotConfig.legendTemplate != null
          ? runsLinePlotConfig.legendTemplate!
          : getDefaultLegendTemplateByRunsetId(runsetId),
    [
      getDefaultLegendTemplateByRunsetId,
      runsLinePlotConfig.legendTemplate,
      isSingleRun,
    ]
  );

  const groupKeysByRunsetId = useMemo(() => {
    return runSets.reduce(
      (acc, runset) => {
        acc[runset.id] = getGroupKeysByRunsetId(runset.id);
        return acc;
      },
      {} as Record<string, Key[]>
    );
  }, [runSets, getGroupKeysByRunsetId]);

  const colorEachMetricDifferently = useColorByMetrics(runsLinePlotConfig);
  const newResult = useMemo(() => {
    return {
      /**
       * runsLinePlotConfig settings - the nullish coalescing operator is used as most of these values won't be explicitly set in a panel unless a user modifies the value which then exposes it to Redux and the external data stores.
       */
      aggregateCalculations,
      aggregateMetrics,
      aggregatePanelRuns,
      colorEachMetricDifferently,
      excludeOutliers,
      groupArea:
        runsLinePlotConfig.groupArea ?? runsLinePlotConfigDefaults.groupArea,
      groupLine:
        runsLinePlotConfig.groupAgg ?? runsLinePlotConfigDefaults.groupAgg,
      groupingDepth: runsLinePlotConfig.groupingDepth,
      legendFields: runsLinePlotConfig.legendFields,
      getLegendTemplateByRunsetId,
      groupKeysByRunsetId,
      limit,
      plotType:
        runsLinePlotConfig.plotType ?? runsLinePlotConfigDefaults.plotType,
      rootUrl:
        entityName != null && projectName != null
          ? `/${entityName}/${projectName}/runs`
          : null,
      showOriginalAfterSmoothing,
      smoothingParam: smoothingWeight,
      smoothingType,
      useRunsTableGroupingInPanels,
      windowing,
      xAxis,
      xAxisFormat,
      yAxis: runsLinePlotConfig.metrics ?? runsLinePlotConfigDefaults.metrics,
      yLogScale,

      /**
       * `data` refers to high level information that's stable throughout the life of a panel
       */
      entityName,
      projectName,

      /**
       * run color and display name overrides. comes from `section` part
       * of view spec, meaning it's scoped to a workspace or panel grid
       */
      customRunColors,
      customRunNames,
      semanticLegendSettings,

      /**
       * other data from ??? sources
       * TODO: figure out where this data comes from and explain it
       */
      expressions: parsedExpressions.expressions ?? [],
      // Only pass in the part of runSets that we need. getLinesFromData memoizes
      // with deep equal.
      runSets,
      singleRun: isSingleRun,
      xExpression: parsedExpressions.xExpression,
      zoomTimestep,
    };
  }, [
    aggregateCalculations,
    aggregateMetrics,
    aggregatePanelRuns,
    colorEachMetricDifferently,
    customRunColors,
    customRunNames,
    groupKeysByRunsetId,
    getLegendTemplateByRunsetId,
    entityName,
    excludeOutliers,
    isSingleRun,
    limit,
    parsedExpressions.expressions,
    parsedExpressions.xExpression,
    projectName,
    runSets,
    runsLinePlotConfig.groupAgg,
    runsLinePlotConfig.groupArea,
    runsLinePlotConfig.groupingDepth,
    runsLinePlotConfig.legendFields,
    runsLinePlotConfig.metrics,
    runsLinePlotConfig.plotType,
    semanticLegendSettings,
    showOriginalAfterSmoothing,
    smoothingType,
    smoothingWeight,
    useRunsTableGroupingInPanels,
    windowing,
    xAxis,
    xAxisFormat,
    yLogScale,
    zoomTimestep,
  ]);

  // useDerivedLinesConfigDebugging(newResult, true);

  return newResult;
};
